// No imports: logging's early error boundary evaluates this module ahead of its listener.

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface RemovableStorage extends StorageLike {
  removeItem(key: string): void;
}

// try/catch: merely reading the global throws in some privacy modes and in sandboxed opaque-origin frames.
export function resolveLocalStorage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function resolveSessionStorage(): Storage | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

/** The stored value, or null when the key is absent or storage refuses the read. */
export function readItem(
  key: string,
  storage: StorageLike | null = resolveLocalStorage(),
): string | null {
  if (storage === null) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/** False when there is no storage or it refused the write (quota, private mode). */
export function writeItem(
  key: string,
  value: string,
  storage: StorageLike | null = resolveLocalStorage(),
): boolean {
  if (storage === null) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/** False when there is no storage or it refused the removal. */
export function removeItem(
  key: string,
  storage: RemovableStorage | null = resolveLocalStorage(),
): boolean {
  if (storage === null) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}
